import React from 'react';
import {
  Sparkle,
  X,
  PaperPlaneTilt,
  Trash,
  CircleNotch,
  Robot,
  User,
} from 'phosphor-react';
import { ChatMessage } from '../types/entities';
import useAiAgent from '../use/useAiAgent';
import { useAiAgentToggle } from '../context/AiAgentContext';
import RoutineSuggestionModal from './modals/RoutineSuggestionModal';
import AiGeneratingLoader from './ai/AiGeneratingLoader';
import usePresence from '../use/usePresence';
import Skeleton, { SkeletonArea } from './ui/Skeleton';
import Aviso from './ui/Aviso';
import Carregando from './ui/Carregando';

function formatTime(isoString: string | undefined) {
  if (!isoString) return '';
  try {
    return new Date(isoString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

interface MessageBubbleProps {
  message: ChatMessage;
}

function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user';

  return (
    // A bolha nova entra sozinha. Sem `stagger` na lista de propósito: reatrasar a
    // conversa inteira a cada resposta seria o oposto do que se quer aqui.
    <div className={`animate-enter flex items-end gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      <div
        className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center
          ${isUser ? 'bg-blush-400' : 'bg-lavender-400'}`}
      >
        {isUser ? (
          <User className="w-3.5 h-3.5 text-white" />
        ) : (
          <Robot className="w-3.5 h-3.5 text-white" />
        )}
      </div>

      <div
        className={`max-w-[78%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words
          ${isUser
            ? 'bg-blush-400 text-white rounded-br-sm'
            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-bl-sm shadow-sm border border-nude-100 dark:border-gray-700'
          }`}
      >
        {message.content}
        {message.createdAt && (
          <span
            className={`block text-[10px] mt-1 ${
              isUser ? 'text-blush-100 text-right' : 'text-nude-300 text-right'
            }`}
          >
            {formatTime(message.createdAt)}
          </span>
        )}
      </div>
    </div>
  );
}

export default function AiAgentChat() {
  const { isOpen, toggleOpen } = useAiAgentToggle();
  const { montado, estado } = usePresence(isOpen, 200);
  const {
    messages,
    input,
    setInput,
    loading,
    error,
    sendMessage,
    clearHistory,
    bottomRef,
    historyLoaded,
    suggestedRoutines,
    dismissSuggestion,
  } = useAiAgent();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* A IA só recomenda: a rotina sugerida no chat passa por aqui antes de ser salva. */}
      <RoutineSuggestionModal
        isOpen={suggestedRoutines.length > 0}
        routines={suggestedRoutines}
        onSaved={dismissSuggestion}
        onDiscard={dismissSuggestion}
      />

      {montado && (
        <div
          data-state={estado}
          /* Cresce do botão da IA, não do centro da tela: origin-bottom-right. */
          className="fixed bottom-36 right-4 z-50 w-[340px] max-w-[calc(100vw-2rem)]
            bg-cream dark:bg-gray-900 rounded-3xl shadow-2xl border border-nude-100 dark:border-gray-700
            flex flex-col overflow-hidden origin-bottom-right
            data-[state=open]:animate-scale-in data-[state=closed]:animate-scale-out
            data-[state=closed]:pointer-events-none"
          style={{ height: '520px' }}
        >
          <div className="bg-gradient-to-r from-blush-400 to-lavender-400 px-4 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
                <Sparkle className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">AutoCare Agent</p>
                <p className="text-white/70 text-xs">Sua assistente de skincare</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={clearHistory}
                title="Limpar histórico"
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
              >
                <Trash className="w-4 h-4" />
              </button>
              <button
                onClick={toggleOpen}
                title="Fechar"
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <Carregando
            quando={!historyLoaded}
            className="flex-1 overflow-y-auto px-3 py-3"
            esqueleto={
              <SkeletonArea className="flex-1 flex flex-col gap-3" label="Carregando a conversa">
                <Skeleton className="h-12 w-3/5 rounded-2xl rounded-bl-sm" />
                <Skeleton className="h-10 w-2/5 self-end rounded-2xl rounded-br-sm" />
                <Skeleton className="h-16 w-4/5 rounded-2xl rounded-bl-sm" />
              </SkeletonArea>
            }
          >
          <div className="flex flex-col gap-3">
            {messages.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-4">
                <div className="w-14 h-14 bg-blush-50 rounded-2xl flex items-center justify-center">
                  <Sparkle className="w-7 h-7 text-blush-400" />
                </div>
                <div>
                  <p className="text-gray-700 dark:text-gray-200 font-semibold text-sm">Olá! Sou o AutoCare Agent</p>
                  <p className="text-nude-400 dark:text-gray-400 text-xs mt-1 leading-relaxed">
                    Posso consultar sua rotina, perfil de pele, registros de progresso e muito mais.
                    Como posso te ajudar?
                  </p>
                </div>
                <div className="flex flex-col gap-1.5 w-full mt-1">
                  {['Qual é minha rotina atual?', 'Como está minha pele?', 'Ver meu progresso'].map(
                    (suggestion) => (
                      <button
                        key={suggestion}
                        onClick={() => setInput(suggestion)}
                        className="text-xs text-blush-500 bg-blush-50 hover:bg-blush-100 px-3 py-2 rounded-xl transition-colors text-left border border-blush-100"
                      >
                        {suggestion}
                      </button>
                    )
                  )}
                </div>
              </div>
            ) : (
              messages.map((msg, idx) => <MessageBubble key={msg.id ?? idx} message={msg} />)
            )}

            {loading && (
              <div className="flex items-end gap-2">
                <div className="w-7 h-7 rounded-full bg-lavender-400 flex items-center justify-center flex-shrink-0">
                  <Robot className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="flex-1 bg-white dark:bg-gray-800 border border-nude-100 dark:border-gray-700 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <AiGeneratingLoader />
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>
          </Carregando>

          <Aviso mensagem={error} className="mx-3 mb-2" />

          <form
            onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
            className="px-3 pb-3 pt-2 flex items-end gap-2 border-t border-nude-100 dark:border-gray-700 bg-white dark:bg-gray-900"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Digite sua mensagem..."
              rows={1}
              disabled={loading}
              className="flex-1 resize-none bg-nude-50 dark:bg-gray-800 border border-nude-200 dark:border-gray-600 rounded-xl px-3 py-2.5
                text-sm text-gray-700 dark:text-gray-200 placeholder-nude-300 dark:placeholder-gray-500 outline-none
                focus:border-blush-300 focus:ring-2 focus:ring-blush-100 transition-colors
                disabled:opacity-50 max-h-28 leading-relaxed"
              style={{ overflowY: 'auto' }}
              onInput={(e) => {
                const t = e.target as HTMLTextAreaElement;
                t.style.height = 'auto';
                t.style.height = `${Math.min(t.scrollHeight, 112)}px`;
              }}
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="flex-shrink-0 w-10 h-10 bg-blush-400 hover:bg-blush-500
                disabled:bg-nude-200 disabled:cursor-not-allowed
                rounded-xl flex items-center justify-center transition-colors active:scale-[0.97]"
            >
              {loading ? (
                <CircleNotch className="w-4 h-4 text-white animate-spin" />
              ) : (
                <PaperPlaneTilt className="w-4 h-4 text-white" />
              )}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
